import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

/**
 * Phase 2D — /instagram/facebook-login/callback (public, no WorkspaceProvider).
 *
 * Facebook Login (response_type=token) returns the access token in the URL
 * fragment (#access_token=...&expires_in=...&state=...), NOT in query params.
 * This page reads window.location.hash and relays the token to the parent window.
 *
 * Three-path relay:
 *  1. postMessage to opener (normal popup with intact opener) — tightened to same origin
 *  2. localStorage relay (broken-opener / tunnel interstitial) — picked up on mount by listener
 *  3. Direct nav to /w/:wsPublicId (mobile fallback) — wsPublicId parsed from state
 *
 * State format: `{stateId}.{wsPublicId}` per MetaOAuthStateService.mint contract.
 * Splitting on the first '.' is safe: stateId is a UUID (no dots), wsPublicId is `ws_<8 base62>`.
 *
 * Error handling:
 *  - FB user-cancel lands as ?error=... query param (not hash) — navigate to / with fb_login_error
 *  - Missing token in hash — navigate to /?fb_login_error=no_token
 */

const RELAY_KEY = 'fb_login_token_relay';

export function FacebookLoginCallbackPage() {
  const navigate = useNavigate();
  // Guard against React 18/19 StrictMode double-invocation of useEffect in dev.
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    // Error case: FB user-cancel returns query params, not hash.
    const queryParams = new URLSearchParams(window.location.search);
    const error = queryParams.get('error');
    if (error) {
      // In a popup the parent watches for the window closing; a dashboard
      // navigation here would leave the popup open and the parent waiting.
      if (window.opener && !window.opener.closed) {
        window.close();
        return;
      }
      navigate(`/?fb_login_error=${encodeURIComponent(error)}`, { replace: true });
      return;
    }

    // FB token-flow returns #access_token=...&expires_in=...&state=...
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const state = params.get('state');

    if (!accessToken || !state) {
      // Same popup rule as the cancel path (Codex round 45): closing lets the
      // parent's close watcher reset instead of waiting on a token forever.
      if (window.opener && !window.opener.closed) {
        window.close();
        return;
      }
      navigate('/?fb_login_error=no_token', { replace: true });
      return;
    }

    // Parse wsPublicId from state (format: `{stateId}.{wsPublicId}`).
    const dotIndex = state.indexOf('.');
    const wsPublicId = dotIndex !== -1 ? state.slice(dotIndex + 1) : null;

    // Relay path 1: postMessage to opener (popup case) — same-origin only.
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        { type: 'FB_LOGIN_TOKEN', accessToken, state },
        window.location.origin,
      );
      window.close();
      return;
    }

    // Relay path 2: localStorage relay (broken-opener / same-tab case).
    // The listener in use-facebook-login-listeners.ts checks localStorage on
    // mount, so this relay works even when the storage event doesn't fire
    // in the same tab.
    try {
      localStorage.setItem(RELAY_KEY, JSON.stringify({ accessToken, state, ts: Date.now() }));
    } catch { /* ignore */ }

    // Relay path 3: direct nav (mobile fallback).
    // Navigate to the dashboard root so the mount-time localStorage check in
    // use-facebook-login-listeners.ts picks up the relay and opens the picker.
    const destination = wsPublicId ? `/w/${wsPublicId}` : '/';
    navigate(destination, { replace: true });
  }, [navigate]);

  return <div>Connecting Instagram via Facebook…</div>;
}

export default FacebookLoginCallbackPage;
